import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import { sanitizeMarkdown } from '../../utils/markdownSanitization';

/**
 * Renders GitHub-flavored markdown with MUI components and sensible spacing.
 * HTML is NOT allowed for safety. Supports: headings, lists, links, code, blockquotes, tables.
 */
const MarkdownRenderer = ({ content }) => {
  const componentsMap = {
    h1({ children }) { return <Typography variant="h4" fontWeight={700}>{children}</Typography>; },
    h2({ children }) { return <Typography variant="h5" fontWeight={700}>{children}</Typography>; },
    h3({ children }) { return <Typography variant="h6" fontWeight={700}>{children}</Typography>; },
    h4({ children }) { return <Typography variant="subtitle1" fontWeight={700}>{children}</Typography>; },
    h5({ children }) { return <Typography variant="subtitle2" fontWeight={700}>{children}</Typography>; },
    h6({ children }) { return <Typography variant="body2" fontWeight={700}>{children}</Typography>; },
    p({ children }) { return <Typography variant="body1">{children}</Typography>; },
    a({ href, children }) { return <MuiLink href={href} target="_blank" rel="noopener noreferrer">{children}</MuiLink>; },
    img({ src, alt }) {
      // ReactMarkdown renders img by default; we can allow http/https/data urls
      return <Box component="img" src={src} alt={alt} sx={{ maxWidth: '100%', borderRadius: 1 }} />;
    },
    ul({ children }) { return <Box component="ul">{children}</Box>; },
    ol({ children }) { return <Box component="ol">{children}</Box>; },
    li({ children }) { return <Box component="li">{children}</Box>; },
    code({ inline, children }) {
      if (inline) {
        return <Box component="code" sx={{ display: 'inline' }}>{children}</Box>;
      }
      return (
        <Box component="pre">
          <Box component="code">{children}</Box>
        </Box>
      );
    },
    blockquote({ children }) { return <Box component="blockquote">{children}</Box>; },
    table({ children }) { return <Box component="table">{children}</Box>; },
    thead({ children }) { return <Box component="thead">{children}</Box>; },
    tbody({ children }) { return <Box component="tbody">{children}</Box>; },
    tr({ children }) { return <Box component="tr">{children}</Box>; },
    th({ children }) { return <Box component="th">{children}</Box>; },
    td({ children }) { return <Box component="td">{children}</Box>; }
  };

  const sanitizedContent = sanitizeMarkdown(content);

  return (
    <Box sx={{
      '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1 },
      '& p': { mb: 2 },
      '& ul, & ol': { pl: 3, mb: 2 },
      '& li': { mb: 0.5 },
      '& pre': {
        p: 2,
        mb: 2,
        overflow: 'auto',
        backgroundColor: (theme) => theme.palette.grey[100],
        borderRadius: 1
      },
      '& code': {
        fontSize: '0.9em',
        backgroundColor: (theme) => theme.palette.grey[100],
        px: 0.5,
        borderRadius: 0.5
      },
      '& blockquote': {
        borderLeft: '4px solid',
        borderColor: 'divider',
        pl: 2,
        ml: 0,
        my: 2,
        color: 'text.secondary'
      },
      '& table': {
        width: '100%',
        borderCollapse: 'collapse',
        mb: 2
      },
      '& th, & td': {
        border: '1px solid',
        borderColor: 'divider',
        textAlign: 'left',
        p: 1
      }
    }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={componentsMap}
      >
        {sanitizedContent}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;


